import type { ProfilePatch, SessionUser } from '@gramas/shared'
import { useEffect } from 'react'
import { create } from 'zustand'
import { api } from '../api/client.js'
import { adoptUser, getMeta, setMeta } from '../db/dexie.js'

const CACHE_KEY = 'profile'

interface ProfileState {
  profile: SessionUser | null
  loading: boolean
  loaded: boolean
}

/**
 * One profile, shared by every screen that shows it.
 *
 * It was a hook with its own state, which was fine while only Settings used it.
 * Now the diary greets you by name from the same record Settings edits, and two
 * independent copies means editing your name in one and being greeted by the
 * old one in the other until the app restarts.
 */
const useStore = create<ProfileState>(() => ({ profile: null, loading: true, loaded: false }))

/**
 * The profile is cached locally because the resting-burn estimate needs height,
 * age and sex, and that calculation has to work on a phone with no signal
 * exactly as it does online. The server stays the authority: a fetch refreshes
 * the cache whenever one succeeds, a failure leaves the last known values.
 */
async function load(): Promise<void> {
  if (useStore.getState().loaded) return
  useStore.setState({ loaded: true })

  const cached = await getMeta<SessionUser | null>(CACHE_KEY, null)
  if (cached && useStore.getState().profile == null) useStore.setState({ profile: cached })
  try {
    const fresh = await api.get<{ user: SessionUser }>('/api/profile')
    // Whoever this session belongs to now owns what is stored on the device.
    // A session restored from the refresh cookie never passes through `login`,
    // so this is the other place the two can be found to disagree.
    await adoptUser(fresh.user.id)
    useStore.setState({ profile: fresh.user })
    await setMeta(CACHE_KEY, fresh.user)
  } catch {
    // Offline, or the session expired and the caller will handle that.
  } finally {
    useStore.setState({ loading: false })
  }
}

/**
 * The profile as of this moment, for code that speaks outside React's render —
 * the sass layer decides whether it may talk at all from `tone`, and it decides
 * inside event handlers, not components.
 */
export function currentProfile(): SessionUser | null {
  return useStore.getState().profile
}

/**
 * Put an account in place without a session — `dev.html` only.
 *
 * The voice reads three things off the profile before it says anything: the
 * register (`tone`), whether cameos are allowed (`isAdmin`), and which
 * account's rotation memory to write. The laboratory has no session to get
 * those from, and a harness that stubbed them itself would be reviewing its
 * own guesses instead of the engine. Nothing in the app calls this.
 */
export function __setProfileForLab(profile: SessionUser | null): void {
  useStore.setState({ profile, loading: false, loaded: true })
}

export async function saveProfile(patch: ProfilePatch): Promise<void> {
  const updated = await api.patch<{ user: SessionUser }>('/api/profile', patch)
  useStore.setState({ profile: updated.user })
  await setMeta(CACHE_KEY, updated.user)
}

export function useProfile(): {
  profile: SessionUser | null
  loading: boolean
  save: (patch: ProfilePatch) => Promise<void>
} {
  const { profile, loading } = useStore()
  useEffect(() => {
    void load()
  }, [])
  return { profile, loading, save: saveProfile }
}
